import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import FileReceiver from '../components/FileReceiver';
import { uploadDocument } from '../api/documentUpload';
import { getLinkInfo } from '../api/clientInfo';
import { resolveBranding, getBrandTextColors } from '../hooks/useBranding';

const MIN_FILE_SIZE_BYTES = 1024; // 1KB minimum - empty files are rejected

const REQUIRED_FILES = [
  { id: 'actaConstitutiva', label: 'Acta Constitutiva', amount: 1, type: 'pdf', tooltip: 'Documento legal que establece la creación de la empresa, incluyendo estatutos y datos de los socios fundadores.' },
  { id: 'constanciaSituacionFiscal', label: 'Constancia de Situación Fiscal', amount: 1, type: 'pdf', tooltip: 'Documento del SAT que acredita el estatus fiscal actual de la empresa, incluyendo RFC y domicilio fiscal.' },
  { id: 'actasAsambleas', label: 'Actas de Asambleas', amount: 1, type: 'pdf', tooltip: 'Documentos que registran las decisiones tomadas en asambleas de accionistas o socios de la empresa.' },
  { id: 'poderes', label: 'Poderes', amount: 1, type: 'pdf', tooltip: 'Documentos notariales que otorgan facultades legales a representantes para actuar en nombre de la empresa.' },
  { id: 'comprobanteDomicilio', label: 'Comprobante de Domicilio', amount: 1, type: 'pdf', tooltip: 'Recibo de servicios (luz, agua, teléfono) o estado de cuenta bancario con antigüedad no mayor a 3 meses.' },
  { id: 'identificacionOficial', label: 'Identificación oficial del apoderado', amount: 1, type: 'pdf', tooltip: 'INE o pasaporte vigente del representante legal de la empresa.' },
  { id: 'acuseReciboDeclaracion', label: 'Acuse de Recibo de Declaración Anual', amount: 1, type: 'pdf', tooltip: 'Comprobante de presentación de la declaración anual ante el SAT del último ejercicio fiscal.' },
  { id: 'declaracionEjercicio', label: 'Declaración del Ejercicio', amount: 1, type: 'pdf', tooltip: 'Declaración anual completa presentada ante el SAT con información financiera y fiscal del ejercicio.' },
  { id: 'estadosFinancierosExcel', label: 'Estados Financieros Declaración Anual (Excel)', amount: 1, type: 'excel', tooltip: 'Estados financieros en formato Excel que coincidan con los presentados en la declaración anual.' },
  { id: 'estadosFinancieros', label: 'Estados Financieros (Contador)', amount: 1, type: 'pdf', tooltip: 'Estados financieros firmados por el contador público, incluyendo balance general y estado de resultados.' },
  { id: 'estadosFinancierosParciales', label: 'Estados Financieros Parciales (Contador)', amount: 1, type: 'pdf', tooltip: 'Estados financieros internos del periodo en curso (parciales), firmados por el contador público, con antigüedad no mayor a 3 meses.' },
  { id: 'cedulaContador', label: 'Cédula Profesional del Contador', amount: 1, type: 'pdf', tooltip: 'Cédula profesional vigente del contador público que firma los estados financieros.' },
  { id: 'curriculumEmpresarial', label: 'Currículum Empresarial', amount: 1, type: 'pdf', tooltip: 'Documento que describe la historia, experiencia y principales proyectos o clientes de la empresa.' },
  { id: 'contrato', label: 'Contrato', amount: 1, type: 'pdf', tooltip: 'Contrato relacionado con la fianza a tramitar, que establece las obligaciones a garantizar.' },
];

const ExpedienteUpload = () => {
  const { linkId } = useParams();
  const [linkInfo, setLinkInfo] = useState(null); // { nombreEmpresa, clientId, files }
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadErrors, setUploadErrors] = useState({}); // Only track errors locally
  const [currentUpload, setCurrentUpload] = useState(null); // Track current upload for loader
  const [uploadedFiles, setUploadedFiles] = useState({}); // Track locally uploaded files in this session

  // White-label branding for this link's org. The org isn't known until the
  // API responds, so the loader can't be branded on a first-ever visit without
  // a subdomain. We cache the branding per link in localStorage so REPEAT
  // visits brand the loader instantly (no Surelia flash for the client).
  const cachedBranding = useMemo(() => {
    try {
      const raw = localStorage.getItem(`branding:link:${linkId}`);
      return raw ? JSON.parse(raw) : null;
    } catch (_) {
      return null;
    }
  }, [linkId]);

  // Prefer the freshly-loaded branding; fall back to the cached one for the
  // loader; finally default to Surelia.
  const branding = resolveBranding(linkInfo?.branding ?? cachedBranding);
  // Same two-color gradient as the login page: one color is enough — fill
  // the missing side from the other; no colors → the Surelia gradient.
  const gradFrom = branding.colorPrimario || branding.colorSecundario;
  const gradTo = branding.colorSecundario || branding.colorPrimario;
  const hasCustomColors = Boolean(gradFrom);
  const pageBgClass = hasCustomColors ? '' : 'bg-gradient-to-br from-azul-surelia to-azul-surelia/80';
  const pageBgStyle = hasCustomColors
    ? { background: `linear-gradient(to bottom right, ${gradFrom}, ${gradTo})` }
    : {};
  // Letter colors derived from the brand itself: the brand hue pushed toward
  // black/white until it clears WCAG contrast over the gradient. Null when
  // there are no custom colors — the default Surelia styling applies.
  const onColors = hasCustomColors ? getBrandTextColors(gradFrom, gradTo) : null;
  const lightBg = Boolean(onColors?.isLight);

  // Brand the browser tab: white-label orgs must not leak "Surelia" to their
  // clients. Repeat visits get the cached branding, so this is instant there.
  useEffect(() => {
    document.title = `Recepción de documentos | ${branding.nombreComercial}`;
  }, [branding.nombreComercial]);

  // Hide environment banner on this page
  useEffect(() => {
    // Find and hide the environment banner (yellow banner at top with z-99999)
    const hideBanner = () => {
      const banner = document.querySelector('.fixed.top-0.bg-yellow-400');
      if (banner) {
        banner.style.display = 'none';
      }
    };

    hideBanner();

    // Set up an observer in case the banner renders after this component
    const observer = new MutationObserver(hideBanner);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      // Show banner again when leaving the page
      const banner = document.querySelector('.fixed.top-0.bg-yellow-400');
      if (banner) {
        banner.style.display = '';
      }
    };
  }, []);

  // Ref to prevent double fetch in StrictMode
  const hasFetchedRef = useRef(false);

  // Fetch link info (client name + files) from API
  useEffect(() => {
    // Prevent double fetch in React StrictMode
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    const fetchLinkInfo = async () => {
      try {
        const data = await getLinkInfo(linkId);
        setLinkInfo(data);
        // Cache branding so the loader is branded on the client's next visit.
        try {
          if (data?.branding) {
            localStorage.setItem(`branding:link:${linkId}`, JSON.stringify(data.branding));
          } else {
            localStorage.removeItem(`branding:link:${linkId}`);
          }
        } catch (_) {
          // localStorage may be unavailable (private mode); ignore.
        }
      } catch (error) {
        console.error('Error fetching link info:', error);
        setError(error.message || 'Link no válido o expirado');
      } finally {
        setIsLoading(false);
      }
    };

    fetchLinkInfo();
  }, [linkId]);

  const handleFilesChange = async (fileId, files) => {
    console.log('ExpedienteUpload - handleFilesChange called:', { 
      fileId, 
      filesCount: files?.length, 
      isUploading,
      fileNames: files?.map(f => f.name)
    });
    
    if (!files || files.length === 0) {
      console.log('ExpedienteUpload - No files provided');
      return;
    }
    
    if (isUploading) {
      console.log('ExpedienteUpload - Upload in progress, ignoring');
      return;
    }
    
    try {
      setIsUploading(true);
      const fileData = files[0];
      const fileName = fileData.name;

      console.log('ExpedienteUpload - Starting upload:', { fileId, fileName, fileSize: fileData.size });

      // Validate file size - reject empty or too small files
      if (fileData.size < MIN_FILE_SIZE_BYTES) {
        throw new Error(`El archivo está vacío o es demasiado pequeño (mínimo ${MIN_FILE_SIZE_BYTES / 1024}KB)`);
      }

      // Set current upload for loader
      setCurrentUpload({
        fileId,
        fileName,
        uploadedAt: Date.now()
      });

      // Clear any previous errors for this file
      setUploadErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fileId];
        return newErrors;
      });

      const uploadResult = await uploadDocument(
        fileData,
        linkInfo.clientId,
        linkId,
        fileId,
        fileName
      );

      console.log('ExpedienteUpload - Upload completed successfully:', { fileId, fileName, result: uploadResult });

      // Track successful upload locally - mark as uploaded immediately
      setUploadedFiles(prev => ({
        ...prev,
        [fileId]: {
          name: fileName,
          uploadedAt: Date.now()
        }
      }));

    } catch (error) {
      console.error('Error uploading file:', error);
      // Only store errors in local state
      setUploadErrors(prev => ({
        ...prev,
        [fileId]: {
          name: files[0].name,
          uploadedAt: Date.now(),
          error: error.message
        }
      }));
    } finally {
      setIsUploading(false);
      setCurrentUpload(null);
    }
  };

  if (isLoading) return (
    <div className={`flex flex-col items-center justify-center min-h-screen ${pageBgClass}`} style={pageBgStyle}>
      {/* Show a logo only when the org's brand is known (cached from a prior
          visit); otherwise a neutral spinner — never the Surelia logo. */}
      {branding.isWhiteLabel && (
        <div className="mb-8">
          <img
            src={branding.logoUrl}
            alt={branding.nombreComercial}
            className="h-12"
          />
        </div>
      )}
      <div
        className={`animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 ${onColors ? '' : 'border-white'}`}
        style={onColors ? { borderColor: onColors.body } : {}}
      ></div>
      <p className={`mt-4 ${onColors ? '' : 'text-white/70'}`} style={onColors ? { color: onColors.muted } : {}}>Cargando...</p>
    </div>
  );

  if (error || !linkInfo) return (
    <div className={`flex flex-col items-center justify-center min-h-screen px-4 ${pageBgClass}`} style={pageBgStyle}>
      {branding.isWhiteLabel && (
        <div className="mb-8">
          <img
            src={branding.logoUrl}
            alt={branding.nombreComercial}
            className="h-12"
          />
        </div>
      )}
      <div className={`backdrop-blur-sm rounded-xl p-8 max-w-md w-full text-center border ${lightBg ? 'bg-black/5 border-black/10' : 'bg-white/10 border-white/20'}`}>
        <h1 className={`text-xl font-semibold mb-2 ${onColors ? '' : 'text-white'}`} style={onColors ? { color: onColors.heading } : {}}>{error || 'Link no válido o expirado'}</h1>
        <p className={onColors ? '' : 'text-white/70'} style={onColors ? { color: onColors.body } : {}}>Recarga la página en un minuto.</p>
        <p className={`text-sm mt-4 ${onColors ? '' : 'text-white/50'}`} style={onColors ? { color: onColors.muted } : {}}>Este límite protege la seguridad de tus documentos.</p>
      </div>
    </div>
  );

  return (
    <div className={`min-h-screen ${pageBgClass}`} style={pageBgStyle}>
      <div className="container mx-auto px-4 py-6">
        <div className="mb-6">
          <div className="flex justify-start items-center">
            <img
              src={branding.logoUrl}
              alt={branding.nombreComercial}
              className="h-12"
            />
          </div>
        </div>
        {linkInfo.nombreEmpresa && (
          <div className="mb-4">
            <h2
              className={`text-2xl font-semibold ${branding.isWhiteLabel && onColors ? '' : branding.isWhiteLabel ? 'text-white' : 'text-gray-300'}`}
              style={branding.isWhiteLabel && onColors ? { color: onColors.heading } : {}}
            >
              Recepción de documentos: {linkInfo.nombreEmpresa}
            </h2>
          </div>
        )}
        <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(linkInfo.files)
            .filter(([_, fileObj]) => fileObj.required)
            .map(([fileId, fileObj]) => {
              const file = REQUIRED_FILES.find(f => f.id === fileId);
              return file ? { fileId, fileObj, label: file.label, file } : null;
            })
            .filter(Boolean)
            .sort((a, b) => a.label.localeCompare(b.label, 'es'))
            .map(({ fileId, fileObj, file }) => {
              // File is uploaded if API says so OR if we uploaded it in this session
              const isUploaded = fileObj.uploaded || uploadedFiles[fileId];
              const localUpload = uploadedFiles[fileId];
              const disableUpload = isUploaded || isUploading;
              const uploadError = uploadErrors[fileId];
              const isCurrentlyUploading = currentUpload?.fileId === fileId;

              return (
                <div key={fileId} className="flex flex-col">
                  <FileReceiver
                    title={file.label}
                    fileCodeName={fileId}
                    onFilesChange={(files) => handleFilesChange(fileId, files)}
                    maxFiles={file.amount}
                    disabled={disableUpload}
                    type={file.type}
                    tooltip={file.tooltip}
                  />
                  <div className="mt-2 flex flex-col gap-2">
                    {/* Show upload loader */}
                    {isCurrentlyUploading && (
                      <div className="flex items-center gap-2 px-3 py-2 rounded border bg-blue-50 border-blue-200">
                        <div className="flex-shrink-0">
                          <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-500 border-t-transparent"></div>
                        </div>
                        <div className="flex-grow">
                          <span className="text-sm text-blue-700">Subiendo {currentUpload.fileName}...</span>
                        </div>
                      </div>
                    )}

                    {/* Show error if exists */}
                    {uploadError && !isCurrentlyUploading && (
                      <div className="flex items-center gap-2 px-3 py-2 rounded border bg-red-50 border-red-200">
                        <div className="flex-grow">
                          <div className="text-sm text-red-700 font-medium">{uploadError.name}</div>
                          <div className="text-xs text-red-600 mt-1">Error: {uploadError.error}</div>
                        </div>
                      </div>
                    )}

                    {/* Show uploaded status */}
                    {isUploaded && !isCurrentlyUploading && (
                      <div className="flex items-center gap-2 px-3 py-2 rounded border bg-green-100 border-green-400 text-green-800">
                        <svg className="h-5 w-5 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="font-medium">
                          {localUpload ? localUpload.name : 'Documento recibido'}
                        </span>
                        <span className="ml-auto text-xs font-semibold">Recibido</span>
                      </div>
                    )}

                    {/* Show pending status */}
                    {!isUploaded && !uploadError && !isCurrentlyUploading && (
                      <div className="px-3 py-2 rounded border bg-gray-50 border-gray-200 text-gray-400 text-xs">
                        No hay archivos subidos
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      </div>
      </div>
    </div>
  );
};

export default ExpedienteUpload; 